import { useEffect, useRef, useContext } from "react";
import { doc } from "firebase/firestore";
import { updatePrimary } from "../helperFunctions/primaryWrites";
import { getDbRef } from "../helperFunctions/determineType";
import { checkPendingDrafts, buildPendingDraftsUpdate } from "../helperFunctions/pendingDraftsCheck";
import { SettingsContext } from "../providers/SettingsProvider";

const DEFAULT_LIMIT = 10;

// Batch counterpart to PrimaryForm's single-record pending-draft check - opportunistically
// resolves pendingDrafts entries for a handful of records when staff land on a list/calendar
// view, rather than only when someone reopens that exact record. Each record's own dbRef is
// resolved from its primaryType, so records from different collections can be mixed in one
// call (needed for Calendar, which shows timesheets/jobsheets/quotes together).
//
// Only runs once per resetKey (e.g. undefined for "once per page mount", or the visible
// calendar week for "once per week viewed") so it doesn't refire on every Firestore snapshot.
export function useBatchPendingDraftsCheck(records, resetKey, limit = DEFAULT_LIMIT) {
  const { draftPollingEnabled } = useContext(SettingsContext);
  let checkedForKey = useRef();
  let hasRunOnce = useRef(false);

  useEffect(() => {
    if (!draftPollingEnabled) return;

    let pending = (records || []).filter((r) => r.hasPendingDrafts && r.pendingDrafts?.length > 0);
    if (pending.length === 0) return;
    if (hasRunOnce.current && checkedForKey.current === resetKey) return;

    hasRunOnce.current = true;
    checkedForKey.current = resetKey;

    pending.slice(0, limit).forEach((record) => {
      let dbRef = getDbRef(record.primaryType);
      if (!dbRef) return;

      checkPendingDrafts(record.pendingDrafts).then((result) => {
        let update = buildPendingDraftsUpdate(record, result);
        if (!update) return;

        updatePrimary(doc(dbRef, record.uid), update).catch((err) => {
          console.error("Failed to apply batch pending draft check results", err);
        });
      });
    });
  }, [records, resetKey, limit, draftPollingEnabled]);
}
